import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { StopCard, isOutdoor } from "../components/StopCard";
import { TravelLeg } from "../components/TravelLeg";
import { VotePanel } from "../components/VotePanel";
import { ItinerarySkeleton } from "../components/ItinerarySkeleton";
import { ItineraryHeader } from "../components/ItineraryHeader";
import { ItineraryBanners } from "../components/ItineraryBanners";
import { ItineraryMap } from "../components/ItineraryMap";
import { useEveningWeather } from "../components/WeatherBadge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { legsForStops, nightSummary } from "../lib/travel";
import { usePlanSource, sharePayload, copyShareLink, voterId, persistPlan } from "../hooks/usePlanSource";

function ShareNotFound({ message }) {
  const navigate = useNavigate();
  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="share-not-found">
        <h1 className="font-display text-3xl font-extrabold">Plan not found</h1>
        <p className="mt-3 text-muted-foreground">{message}</p>
        <Button data-testid="share-not-found-plan-btn" onClick={() => navigate("/")} className="mt-8 rounded-full cta-gradient text-white font-semibold">
          <Sparkles size={16} className="mr-2" /> Plan your own night
        </Button>
      </main>
    </>
  );
}

function swappedStop(stop, candidate, reason) {
  return {
    ...stop,
    name: candidate.name,
    address: candidate.address,
    lat: candidate.lat,
    lon: candidate.lon,
    icon: candidate.icon,
    category_label: candidate.category_label,
    outdoor: candidate.outdoor,
    reason,
  };
}

export default function Itinerary({ shared = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, setPlan, pending, shareId, error } = usePlanSource(shared);
  const weather = useEveningWeather(plan?.city_center || plan?.stops?.[0], plan?.date);

  const [activeIndex, setActiveIndex] = useState(null);
  const [swappingIndex, setSwappingIndex] = useState(null);
  const [cursors, setCursors] = useState({ dinner: 0, drinks: 0 });
  const [busy, setBusy] = useState(null); // "save" | "share" | "vote"

  if (pending && !plan) return <ItinerarySkeleton city={pending.city} />;
  if (shared && error) return <ShareNotFound message={error} />;
  if (!plan) {
    return shared ? (
      <>
        <Header />
        <main className="flex justify-center py-32 text-muted-foreground" data-testid="share-loading">
          <Loader2 className="animate-spin" />
        </main>
      </>
    ) : null;
  }

  const stops = plan.stops;
  const legs = legsForStops(stops);
  const myVotes = plan.votes?.[voterId()] || {};
  const rainLikely = !!weather?.rain_likely;

  const handleSwap = async (index) => {
    const type = stops[index].type;
    const list = plan.candidates?.[type] || [];
    if (!list.length) return toast.error("No other venues nearby to swap to.");
    setSwappingIndex(index);
    const candidate = list[cursors[type] % list.length];
    try {
      const { data } = await api.post("/plan/reason", {
        stop_type: type, name: candidate.name, category: candidate.category_label,
        city: plan.city, vibe: plan.vibe, budget: plan.budget,
      });
      const newPlan = { ...plan, stops: stops.map((s, i) => (i === index ? swappedStop(s, candidate, data.reason) : s)) };
      setPlan(newPlan);
      persistPlan(newPlan);
      setCursors((c) => ({ ...c, [type]: c[type] + 1 }));
      toast.success(`Swapped to ${candidate.name}`);
    } catch {
      toast.error("Could not swap venue. Try again.");
    } finally {
      setSwappingIndex(null);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.info("Log in to save your night.");
      return navigate("/auth");
    }
    setBusy("save");
    try {
      await api.post("/nights", sharePayload(plan));
      toast.success("Saved to My Nights!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(null);
    }
  };

  const handleShare = async () => {
    setBusy("share");
    try {
      await copyShareLink(plan);
    } catch {
      toast.error("Could not create share link.");
    } finally {
      setBusy(null);
    }
  };

  const handleVote = async (stopType, optionIndex) => {
    setBusy("vote");
    try {
      const { data } = await api.post(`/share/${shareId}/vote`, { voter_id: voterId(), stop_type: stopType, option_index: optionIndex });
      setPlan((p) => ({ ...p, votes: data.votes, tallies: data.tallies, options: data.options, planned: data.planned }));
      toast.success(`Voted for ${plan.options[stopType][optionIndex].name}`);
    } catch {
      toast.error("Could not record your vote.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ItineraryHeader
          plan={plan}
          shared={shared}
          summary={nightSummary(stops)}
          weather={weather}
          outdoorCount={stops.filter(isOutdoor).length}
          onBack={() => navigate("/")}
          onShare={handleShare}
          onSave={handleSave}
          sharing={busy === "share"}
          saving={busy === "save"}
        />
        <ItineraryBanners plan={plan} shared={shared} />

        <div className="mt-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="order-1 lg:order-2 w-full lg:w-1/2">
            <div className="h-[360px] lg:h-[calc(100vh-140px)] lg:sticky lg:top-24 rounded-2xl overflow-hidden border border-border shadow-2xl">
              <ItineraryMap stops={stops} center={plan.city_center} activeIndex={activeIndex} />
            </div>
          </div>

          <div className="order-2 lg:order-1 w-full lg:w-1/2 flex flex-col gap-6">
            {stops.map((stop, i) => (
              <div key={`${stop.type}-${i}`} className="flex flex-col gap-6">
                {i > 0 && <TravelLeg leg={legs[i - 1]} index={i - 1} />}
                <StopCard
                  stop={shared ? { ...stop, swappable: false } : stop}
                  index={i}
                  active={activeIndex === i}
                  onHover={setActiveIndex}
                  onSwap={handleSwap}
                  swapping={swappingIndex === i}
                  rainLikely={rainLikely}
                />
                {shared && plan.options?.[stop.type] && (
                  <VotePanel
                    stopType={stop.type}
                    options={plan.options[stop.type]}
                    tallies={plan.tallies?.[stop.type]}
                    plannedIndex={plan.planned?.[stop.type] ?? 0}
                    myVote={myVotes[stop.type]}
                    onVote={handleVote}
                    voting={busy === "vote"}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
